
import stampit from 'stampit'

import Clip from './clip'
import Face from './face'
import EventEmitter from 'events'

const Scene = stampit({
  name: 'Scene',
  props: {
    name: null,
    clips: []
  },
  init ({clips, name}) {
    this.clips = [] // Why isn't this getting initialized by the props?
    if (clips && clips.length) {
      for (var i = 0; i < clips.length; ++i) {
        // to add clips this way, we need to call getUniqueFaces at the end
        this.clips.push(Clip(clips[i]))
      }
    }
    this.name = name
  },
  statics: {
    randomPlayId: 0,
    eventEmitter: new EventEmitter(),
    stop: function () {
      Scene.randomPlayId = 0
      Clip.stop()
    },
    isPlaying: function () {
      return Clip.isPlaying()
    },
    getPreviewText: function (sceneObject) {
      var text = ''
      if (sceneObject.clips && sceneObject.clips.length) {
        for (var i = 0; i < sceneObject.clips.length; ++i) {
          if (i > 0) {
            text += ' '
          }
          if (sceneObject.clips[i].utterances && sceneObject.clips[i].utterances.length) {
            for (var j = 0; j < sceneObject.clips[i].utterances.length; ++j) {
              if (j > 0) {
                text += ' '
              }
              text += sceneObject.clips[i].utterances[j].text
            }
          }
        }
        if (text.length > 50) {
          text = text.substring(0, 50)
          text += '...'
        }
      }
      return text
    }
  },
  methods: {
    isEmpty () {
      if (this.clips.length === 0) {
        return true
      } else if (this.clips.length === 1) {
        // Detect empty clip
        return this.clips[0].utterances.length === 0
      }
      return false
    },
    hasInconsistentVoice (faceId, voice, skipIndex) {
      for (var i = 0; i < this.clips.length; ++i) {
        if (i !== skipIndex && this.clips[i].faceId === faceId) {
          if (this.clips[i].voice.preferred && voice.preferred) {
            if (this.clips[i].voice.preferred[0] !== voice.preferred[0]) {
              console.log(i + ' ' + this.clips[i].voice.preferred[0])
              return true
            }
          }
        }
      }

      return false
    },
    makeVoiceConsistent (faceId, voice) {
      for (var i = 0; i < this.clips.length; ++i) {
        if (this.clips[i].faceId === faceId) {
          this.clips[i].voice = voice
        }
      }
    },
    addClip (clip) {
      if (typeof clip === 'object' && clip !== null) {
        this.clips.push(clip)
        this.getUniqueFaces()
      } else {
        throw new Error('unknown clip type')
      }

      return this
    },

    deleteClip (index) {
      if (index < this.clips.length && index >= 0) {
        this.clips.splice(index, 1)
      }
      this.getUniqueFaces()
      return this
    },
    getUniqueFaces () {
      var faces = {}
      for (var i = 0; i < this.clips.length; ++i) {
        faces[this.clips[i].faceId] = true
      }
      return Object.keys(faces)
    },
    getClip (index) {
      if (index < this.clips.length && index >= 0) {
        return Clip(this.clips[index])
      }
      throw new Error('index out of bounds')
    },
    getClipFaceId (index) {
      if (index >= this.clips.length || index < 0) {
        index = 0
      }
      if (this.clips.length > 0) {
        var clip = this.clips[index]
        if (clip) {
          return clip.faceId
        }
      }
      return Face.defaultId
    },
    getPreviewText () {
      var text = ''
      for (var i = 0; i < this.clips.length; ++i) {
        if (i > 0) {
          text += ' '
        }
        text += this.clips[i].text()
      }
      if (text.length > 50) {
        text = text.substring(0, 50)
        text += '...'
      }
      return text
    },
    playScene (onFinished, maxDelay) {
      Scene.randomPlayId = Math.random()
      if (this.clips.length === 0) {
        if (onFinished) {
          onFinished()
        }
        return
      }
      let clipIndex = 0
      let localPlayId = Scene.randomPlayId
      let endIndex = this.clips.length - 1

      let onPlayNextClip = null
      let playClipAtIndex = (clipIndex) => {
        if (localPlayId === Scene.randomPlayId) {
          let clip = this.getClip(clipIndex)
          if (clip) {
            Scene.eventEmitter.emit('clipstart', clip)
            clip.play(onPlayNextClip, null, maxDelay)
          }
        }
      }
      onPlayNextClip = () => {
        if (clipIndex >= endIndex && localPlayId === Scene.randomPlayId) {
          Scene.eventEmitter.emit('scenefinished')
          if (onFinished) onFinished()
          return
        }
        clipIndex += 1
        playClipAtIndex(clipIndex)
      }

      playClipAtIndex(0)
    },
    playClips (startIndex, endIndex, maxDelay) {
      Scene.randomPlayId = Math.random()
      var localPlayId = Scene.randomPlayId
      if (startIndex === undefined) {
        startIndex = 0
      } else if (typeof (startIndex) === 'string') {
        startIndex = parseInt(startIndex)
      }

      if (endIndex === undefined) {
        endIndex = this.clips.length - 1
      } else if (typeof (endIndex) === 'string') {
        endIndex = parseInt(endIndex)
      }
      if (endIndex >= this.clips.length) {
        endIndex = this.clips.length - 1
      }
      if (endIndex < startIndex || startIndex < 0) {
        throw new Error('start or end index out of bounds')
      }

      let onPlayNextClip = null

      let onLastUtteranceEndPrediction = () => {
        if (localPlayId === Scene.randomPlayId) {
          Scene.eventEmitter.emit('lastUttereranceEndPrediction')
        }
      }

      let playClipAtIndex = (clipIndex) => {
        if (localPlayId === Scene.randomPlayId) {
          let clip = this.getClip(clipIndex)
          if (clip) {
            Scene.eventEmitter.emit('clipstart', clip)
            clip.play(onPlayNextClip, null, maxDelay)
          }
          if (clipIndex >= endIndex) {
            Clip.eventEmitter.addListener('lastUttereranceEndPrediction', onLastUtteranceEndPrediction)
          }
        }
      }
      let clipIndex = startIndex

      Clip.eventEmitter.removeAllListeners('lastUttereranceEndPrediction')

      onPlayNextClip = () => {
        if (clipIndex >= endIndex) {
          Scene.eventEmitter.emit('scenefinished')
          return
        }
        clipIndex += 1
        playClipAtIndex(clipIndex)
      }
      playClipAtIndex(startIndex)
    }
  }
})

export default Scene



// WEBPACK FOOTER //
// ./src/tts/scene.js